<template>
  <div class="relative w-full reader_content_pane" ref="readerContent">
    <div
      class="relative h-full w-full sm:max-w-auto"
      :class="{
        'overflow-y-auto overflow-x-hidden mx-auto cursor-pointer disable-scrollbars':
          isVls,
        'pointer-events-none': onPlaceholder,
      }"
      ref="stripContainer"
      @scroll="throttledScroll"
    >
      <component
        v-if="!switchingChapters || !isVls"
        :is="isVls ? 'div' : Swiper"
        v-bind="isVls ? {} : swiperAttrs"
      >
        <SwiperSlide
          v-for="page in adjustedPageArray"
          :key="`slide-${page.chapterId}-${page.id}`"
          :id="`slide-${page.chapterId}-${page.id}`"
          :class="{
            '!flex justify-center': !isVls,
          }"
        >
          <Reader2ImagePlaceholder
            v-if="isPlaceholderPageGroup(page)"
            :page="page"
            :container-dimensions="swiperDimensions"
          />
          <Reader2Image
            v-else-if="isImagePageGroup(page)"
            :reader-settings="readerSettings"
            class="!overflow-visible"
            :class="{ 'swiper-zoom-container': !isVls }"
            :image="page"
            :container-dimensions="swiperDimensions"
            :sourceReadingDirection
          />
          <Reader2Interlude
            v-else-if="
              isInterludePageGroup(page) && page.nextChapterId && page.chapterId
            "
            :page="page"
            @next-chapter="doNextChapter(page.nextChapterId)"
            @open-comments="commentsOpen = true"
          />
          <Reader2EndCard
            v-else-if="isInterludePageGroup(page) && page.type === 'end'"
            :page="page"
          />
          <Reader2SubscribeCard
            v-else-if="isSubscribePageGroup(page)"
            :page="page"
          />
          <div v-else>{{ page }}</div>
        </SwiperSlide>
      </component>
      <NamiLoading v-else />
    </div>
    <div
      v-if="showScrollToTopButton && isVls"
      class="absolute z-[3] right-8 bottom-24 pointer-events-auto"
    >
      <NamiButton
        buttonType="primary"
        :icon="IconChevronUp"
        pill
        @click.stop
        @buttonClick="goToTop"
      />
    </div>
    <Transition
      enter-active-class="transition-all"
      enter-from-class="scale-95 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-active-class="transition-all"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-95 opacity-0"
    >
      <div
        class="absolute flex justify-center z-[11] bottom-8 right-0 w-full pointer-events-none"
        v-if="_isZoomed"
        @click.stop
      >
        <div
          class="p-4 rounded-full bg-neutral-200/80 dark:bg-neutral-800/80 backdrop-blur-lg shadow-xl flex divide-x-2 divide-neutral-300 dark:divide-neutral-700 pointer-events-auto"
        >
          <div class="my-auto text-xl w-20 text-center">{{ currentZoom }}x</div>
          <div class="px-2">
            <NamiButton
              buttonType="primary"
              text
              pill
              :icon="IconZoomOut"
              @buttonClick="decreaseZoom"
              :disabled="currentZoom <= 1"
            />
            <NamiButton
              buttonType="primary"
              text
              pill
              :icon="IconZoomIn"
              @buttonClick="increaseZoom"
              :disabled="currentZoom >= 5"
            />
          </div>
          <NamiButton
            class="px-2"
            buttonType="secondary"
            text
            pill
            :icon="IconX"
            @buttonClick="exitZoom"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/zoom";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/virtual";

import {
  isContentPageGroup,
  isImagePageGroup,
  isInterludePageGroup,
  isPlaceholderPageGroup,
  isSubscribePageGroup,
} from "~/src/reader/utils";
import { FreeMode, Mousewheel, Virtual, Zoom } from "swiper/modules";
import { Swiper as SwiperClass } from "swiper";
import type { UnwrapNestedRefs } from "vue";
import type { ReaderManager } from "~/src/reader/managers/reader";
import type { ReaderSettingsInterface } from "~/src/reader/static";
import { type TitleReadingMode } from "~/src/api";
import { useQueryClient } from "@tanstack/vue-query";
import {
  IconChevronUp,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-vue";
import { clamp, useEventListener, useThrottleFn } from "@vueuse/core";

const queryClient = useQueryClient();

const {
  readerSettings,
  readerManager,
  sourceReadingDirection,
  solo,
  disabled,
  lsDragging,
} = defineProps<{
  readerManager: UnwrapNestedRefs<ReaderManager>;
  readerSettings: UnwrapNestedRefs<ReaderSettingsInterface>;
  sourceReadingDirection: TitleReadingMode;
  solo?: boolean;
  disabled?: boolean;
  lsDragging?: boolean;
}>();

const readerContent = ref<HTMLDivElement>();
const stripContainer = ref<HTMLDivElement>();

const {
  commentsOpen,
  scrollDirection,
  readingDirection,
  readingMode,
  animation,
  _isZoomed,
  scrollWheelBehavior,
} = toRefs(readerSettings);

const isVls = computed(
  () =>
    sourceReadingDirection === "vls" || scrollDirection.value === "vertical",
);
const computedReadingDirection = computed<"ltr" | "rtl">(() => {
  if (scrollDirection.value === "vertical") return "ltr";
  if (readingDirection.value !== "content") return readingDirection.value;
  if (sourceReadingDirection === "vls") return "ltr";
  return sourceReadingDirection;
});

const swiperInstance = ref<SwiperClass>();
const swiperDimensions = ref<{ width: number; height: number }>();

const slide = (index: number) => {
  const instance = swiperInstance.value;
  if (!instance) return;
  if (instance.destroyed) return;
  try {
    instance.slideTo(index, blockScrollChanges.value ? undefined : 0, false);
  } catch (e) {
    console.error(e);
  }
};

const forward = ref(true);
watch(
  () => readerManager.currentPageIndex,
  (realIndex, old) => {
    forward.value = realIndex >= old;
    const instance = swiperInstance.value;
    if (sourceReadingDirection === "vls") return;

    if (isVls.value && !blockScrollChanges.value) {
      const currPage = readerManager.pageArray[readerManager.currentPageIndex];
      const page = document.getElementById(
        `slide-${currPage.chapterId}-${currPage.id}`,
      );
      page?.scrollIntoView();
    }

    // run always when blocking scroll changes
    if (instance) {
      // this is to smooth out any jumps when stuff gets loaded mid-transition
      // dont run when scroll changes are blocked, leads to jumping around as animation end triggers fire
      if (instance.animating && instance.realIndex !== realIndex) {
        instance.once("transitionEnd", () => slide(realIndex));
      } else slide(realIndex);
    }
  },
);

watch(scrollDirection, (dir) => {
  if (dir === "vertical") {
    nextTick(() => {
      const currPage = readerManager.pageArray[readerManager.currentPageIndex];
      const page = document.getElementById(
        `slide-${currPage.chapterId}-${currPage.id}`,
      );
      page?.scrollIntoView();
    });
  }
});

function updateSwiperDimensions(swiper: SwiperClass) {
  swiperDimensions.value = {
    width: swiper.width,
    height: swiper.height,
  };
}

const setNewIndex = (swiper: SwiperClass) => {
  if (
    blockScrollChanges.value ||
    actionLoading.value ||
    readingModeSwitching.value
  )
    return;
  if (!isNaN(swiper.realIndex))
    readerManager.currentPageIndex = swiper.realIndex;
  if (solo) return;
};

const adjustedPageArray = computed(() => {
  if (isVls.value) {
    const currCh = readerManager.getCurrChapterId();
    return readerManager.pageArray.filter((page) => page.chapterId === currCh);
  }

  return readerManager.pageArray;
});
const onPlaceholder = computed(() => {
  const cpg = adjustedPageArray.value.at(readerManager.currentPageIndex);
  return !!(cpg && isPlaceholderPageGroup(cpg));
});

watch(onPlaceholder, (v) => {
  const cpg = adjustedPageArray.value.at(readerManager.currentPageIndex);
  if (v && cpg) {
    loadUnload(cpg.chapterId);
  }
});

// ONLY USED FOR HORIZONTAL PAGED READING
const swiperAttrs = computed(() => ({
  class: "h-full cursor-pointer",
  modules: [Mousewheel, Virtual, FreeMode, Zoom],
  mousewheel: { forceToAxis: true },
  speed: animation.value ? 300 : 0,
  direction: "horizontal",
  slidesPerView: 1,
  virtual: {
    enabled: true,
    cache: false,
  },
  allowTouchMove: !onPlaceholder.value,
  zoom: { maxRatio: 2 },
  initialSlide: Math.floor(Math.max(readerManager.currentPageIndex, 0)),
  onInit: (swiper: SwiperClass) => {
    swiperInstance.value = swiper;
    swiper.changeLanguageDirection(computedReadingDirection.value);
  },
  resizeObserver: true,
  onSlideChange: setNewIndex,
  onResize: updateSwiperDimensions,
  onZoomChange: processZoom,
}));

const { action: loadUnload, pending: actionLoading } = useAction2(
  async (chapterId?: string) => {
    if (!chapterId) return;
    if (!readerManager.rawPageMap.has(chapterId)) {
      await readerManager.fetchChapterPages(() => queryClient, chapterId);
      readerManager.loadChapterPages(chapterId, !forward.value);
    }

    if (forward.value) {
      readerManager.currentPageIndex = readerManager.pageArray.findIndex(
        (page) => isContentPageGroup(page) && page.chapterId === chapterId,
      );
    } else {
      readerManager.currentPageIndex = readerManager.pageArray.findLastIndex(
        (page) => isContentPageGroup(page) && page.chapterId === chapterId,
      );
    }

    await nextTick();
    slide(readerManager.currentPageIndex);
  },
);

function doNextChapter(nextId: string) {
  if (isVls.value) {
    switchChapter(nextId);
  } else swiperInstance.value?.slideNext();
}

const preloadNextChapter = () => {
  const next = readerManager.getNextChapterId();
  if (next) readerManager.fetchChapterPages(() => queryClient, next);
};

// zoom handling
const currentZoom = ref(0);

function processZoom(swiper: SwiperClass) {
  setTimeout(() => {
    if (swiper.zoom.scale > 1) {
      if (currentZoom.value > 1) {
        return;
      }

      enterZoom();
    } else {
      exitZoom();
    }
  }, 300);
}

function enterZoom() {
  assertDefined(swiperInstance);

  _isZoomed.value = true;
  currentZoom.value = 2;
  swiperInstance.value.zoom.in(currentZoom.value);
  // swiperInstance.value.disable()
}

function exitZoom() {
  _isZoomed.value = false;
  currentZoom.value = 1;
  swiperInstance.value?.zoom.out();
  // swiperInstance.value?.enable()
}

function increaseZoom() {
  assertDefined(swiperInstance);

  currentZoom.value = currentZoom.value + 1;
  swiperInstance.value.zoom.in(currentZoom.value);
}

function decreaseZoom() {
  assertDefined(swiperInstance);

  currentZoom.value = currentZoom.value - 1;
  swiperInstance.value.zoom.in(currentZoom.value);
}

// This hack emulates a double-click into the current mouse position.
// It helps us enter zoom into the current position of the mouse without double clicking
function emitPointerDownEventAtMouseLocation(x: number, y: number) {
  const swiperWrapper = document.querySelector(".swiper-wrapper");
  assertDefined(swiperWrapper);

  const event1 = new MouseEvent("pointerdown", {
    view: window,
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
  });
  const event2 = new MouseEvent("pointerup", {
    view: window,
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
  });

  swiperWrapper.dispatchEvent(event1);
  swiperWrapper.dispatchEvent(event2);
  setTimeout(() => {
    swiperWrapper.dispatchEvent(event1);
    swiperWrapper.dispatchEvent(event2);
  }, 10);
}

function zoomInOrEnterZoom(e: WheelEvent) {
  if (currentZoom.value === 1) {
    emitPointerDownEventAtMouseLocation(e.clientX, e.clientY);

    currentZoom.value = 2;
    _isZoomed.value = true;
  } else if (currentZoom.value < 5) {
    increaseZoom();
  }
}

function zoomOutOrExitZoom(e: WheelEvent) {
  if (currentZoom.value === 2) {
    emitPointerDownEventAtMouseLocation(e.clientX, e.clientY);

    currentZoom.value = 1;
    _isZoomed.value = false;
  } else if (currentZoom.value > 2) {
    decreaseZoom();
  }
}

// reader scroll
const blockScrollChanges = ref(false);
const showScrollToTopButton = ref(false);
const oldScroll = ref(0);
const lsProgress = defineModel("lsProgress", { default: 0 });
const widthResizing = ref(false);

// once a frame @ 60fps
const throttledScroll = useThrottleFn(handleScroll, 16);

// this modifies lsProgress without modifying scroll position
function handleScroll() {
  const container = stripContainer.value;
  if (!container || blockScrollChanges.value || widthResizing.value) return;
  const slideElements = Array.from(
    container.children[0]?.children,
  ) as HTMLElement[];
  const dimensions = slideElements.map((elem) => {
    return {
      top: elem.offsetTop,
      left: elem.offsetLeft,
      height: elem.scrollHeight,
      width: elem.scrollWidth,
    };
  });

  lsProgress.value =
    container.scrollTop / (container.scrollHeight - container.offsetHeight);

  // calculate cursor position
  const cursor = clamp(
    container.offsetHeight * lsProgress.value,
    0,
    container.offsetHeight,
  ).round();

  // find the index of current page based on cursor position
  const idx = dimensions.findIndex(
    (dims) =>
      dims.top <= container.scrollTop + cursor &&
      dims.top + dims.height >= container.scrollTop + cursor,
  );
  const currChapterId = readerManager.getCurrChapterId();
  const offsetIdx = readerManager.pageArray.findIndex(
    (page) => page.chapterId === currChapterId,
  );

  // update the reader index
  blockScrollChanges.value = true;
  if (idx < 0)
    readerManager.currentPageIndex = slideElements.length - 1 + offsetIdx;
  else readerManager.currentPageIndex = idx + offsetIdx;
  nextTick(() => (blockScrollChanges.value = false));

  // preload next once we get near 1 full container height from the bottom
  if (container.scrollHeight - container.scrollTop < container.offsetHeight * 2)
    preloadNextChapter();

  // Scroll up 50px
  if (container.scrollTop > 50 && container.scrollTop < oldScroll.value - 50) {
    showScrollToTopButton.value = true;
    oldScroll.value = container.scrollTop;
    // Scroll down 50px
  } else if (container.scrollTop > oldScroll.value + 50) {
    showScrollToTopButton.value = false;
    oldScroll.value = container.scrollTop;
    // Or we're close to the top
  } else if (container.scrollTop <= 50) {
    showScrollToTopButton.value = false;
    oldScroll.value = 0;
  }
}

useEventListener(stripContainer, "wheel", (e: WheelEvent) => {
  if (isVls.value) return;
  assertDefined(swiperInstance);

  const totalPages = readerManager.pageArray.length;

  if (swiperInstance.value.activeIndex > totalPages) {
    return;
  }

  // @ts-ignore, TODO: wheelDeltaY is deprecated
  const isTrackPad = e.wheelDeltaY
    ? // @ts-ignore
      e.wheelDeltaY === -3 * e.deltaY
    : e.deltaMode === 0;

  if (isTrackPad) {
    return;
  }

  switch (scrollWheelBehavior.value) {
    case "pageTurn": {
      const currPage = readerManager.pageArray[readerManager.currentPageIndex];
      if (currPage && isInterludePageGroup(currPage)) return;

      if (e.deltaY > 50) {
        swiperInstance.value.slideTo(readerManager.currentPageIndex + 1);
      } else if (e.deltaY < -50) {
        swiperInstance.value.slideTo(readerManager.currentPageIndex - 1);
      }

      break;
    }
    case "zoom": {
      if (swiperInstance.value.activeIndex == 0) {
        return;
      }

      if (e.deltaY > 50) {
        zoomOutOrExitZoom(e);
      } else if (e.deltaY < 50) {
        zoomInOrEnterZoom(e);
      }
    }
  }
});

function updateLsProgress(v: number) {
  const container = stripContainer.value;
  if (!container) return;
  container.scrollTo(0, (container.scrollHeight - container.offsetHeight) * v);
  lsProgress.value = v;
}

watch(lsProgress, (v) => {
  if (lsDragging) updateLsProgress(v);
});

// keeps ls position when content pane gets resized
let blockTimeout: NodeJS.Timeout | string | number | undefined = undefined;
const stripObserver = new ResizeObserver(() => {
  swiperInstance.value?.virtual?.update(true);
  blockScrollChanges.value = true;
  updateLsProgress(lsProgress.value);

  if (readerContent.value) {
    swiperDimensions.value = {
      width: readerContent.value.clientWidth,
      height: readerContent.value.clientHeight,
    };
  }
  nextTick().then(() => {
    clearTimeout(blockTimeout);
    blockTimeout = setTimeout(() => {
      blockScrollChanges.value = false;
    }, 150);
  });
});

onMounted(() => {
  if (readerContent.value) stripObserver.observe(readerContent.value);
});

onUnmounted(() => {
  stripObserver.disconnect();
});

function goToTop(instant = false) {
  blockScrollChanges.value = true;
  stripContainer.value?.scrollTo({
    top: 0,
    behavior: animation.value && !instant ? "smooth" : "auto",
  });

  setTimeout(() => {
    blockScrollChanges.value = false;
  }, 500);
}

const { action: switchChapter, pending: switchingChapters } = useAction2(
  async (chapterId: string) => {
    if (!readerManager.chapterIdSequence.length) return;

    lsProgress.value = 0;
    goToTop(true);

    await readerManager.fetchChapterPages(() => queryClient, chapterId);
    await nextTick();

    readerManager.currentPageIndex = readerManager.pageArray.findIndex(
      (page) => page.chapterId === chapterId && isContentPageGroup(page),
    );

    readerManager.loadChapterPages(chapterId);
  },
);

const readingModeSwitching = ref(false);

// handle double/single page
watch(
  readingMode,
  async (v) => {
    if (switchingChapters.value) return;
    readingModeSwitching.value = true;
    switch (v) {
      case "single":
        await readerManager.setPagesPerGroup(1);
        break;
      case "double":
        await readerManager.setPagesPerGroup(2);
        break;
    }

    await nextTick();
    slide(readerManager.currentPageIndex);
    readingModeSwitching.value = false;
  },
  { immediate: true },
);

defineExpose({
  nextPage: () => {
    if (isVls.value) {
      if (!readerContent.value) return;
      stripContainer.value?.scrollBy({
        top: readerContent.value.clientHeight / 2,
        behavior: animation.value ? "smooth" : "auto",
      });
      return;
    }

    const swiper = swiperInstance.value;
    if (!swiper) return;
    swiper.slideNext();
  },
  prevPage: () => {
    if (isVls.value) {
      if (!readerContent.value) return;
      stripContainer.value?.scrollBy({
        top: -readerContent.value.clientHeight / 2,
        behavior: animation.value ? "smooth" : "auto",
      });
      return;
    }

    const swiper = swiperInstance.value;
    if (!swiper) return;
    swiper.slidePrev();
  },
  nextChapter: () => {
    const nextId = readerManager.getNextChapterId();
    if (!nextId) return;
    return switchChapter(nextId);
  },
  prevChapter: () => {
    const nextId = readerManager.getPrevChapterId();
    if (!nextId) return;
    return switchChapter(nextId);
  },
  switchChapter,
});
</script>
